import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

// The full-bleed portrait splash art (palm trees + delivery scooter + tagline).
// Served locally so boot is instant and offline-safe.
const SPLASH_ART = "/eats-gnd-splash.png";

export default function SplashScreen() {
  const navigate = useNavigate();
  const { user, checking } = useAuth();

  useEffect(() => {
    // Give the tropical art a beat to breathe (3.2s) then route the user.
    const timer = setTimeout(() => {
      if (checking) return;
      if (user) {
        if (user.role === "driver") navigate("/driver");
        else if (user.role === "restaurant") navigate("/restaurant-admin");
        else if (user.role === "admin") navigate("/admin");
        else navigate("/home");
      } else {
        navigate("/login");
      }
    }, 3200);
    return () => clearTimeout(timer);
  }, [user, checking, navigate]);

  return (
    <div
      data-testid="splash-screen"
      className="fixed inset-0 w-screen h-screen overflow-hidden bg-black"
      style={{
        // Ensure the splash fills every device edge with no white letterboxing.
        // The safe-area insets are ignored so the logo covers the full physical screen.
        paddingTop: 0,
        paddingBottom: 0,
      }}
    >
      {/* Full-bleed portrait splash art. `object-cover` guarantees edge-to-edge
          coverage on every device — no white borders, no letterboxing.
          `object-position: center top` keeps the "eats GND" wordmark visible
          on ultra-wide desktops where the bottom is cropped. */}
      <img
        src={SPLASH_ART}
        alt="Eats GND — From Grenada with love. Crave it. We'll bring it."
        className="absolute inset-0 w-full h-full object-cover fade-up"
        style={{ objectPosition: "center top" }}
        draggable={false}
      />

      {/* Tap-to-skip layer — sits above the image so any tap advances the app. */}
      <button
        onClick={() => navigate(user ? "/home" : "/login")}
        aria-label="Skip splash"
        data-testid="splash-skip"
        className="absolute inset-0 w-full h-full bg-transparent"
      />

      {/* Bottom loading dots — sit inside the safe area at the bottom. */}
      <div
        className="absolute left-0 right-0 flex justify-center pointer-events-none"
        style={{ bottom: "max(1.5rem, env(safe-area-inset-bottom))" }}
      >
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-white/90 animate-pulse" />
          <span className="w-2 h-2 rounded-full bg-white/70 animate-pulse [animation-delay:120ms]" />
          <span className="w-2 h-2 rounded-full bg-white/50 animate-pulse [animation-delay:240ms]" />
        </div>
      </div>
    </div>
  );
}
